import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Grid, Card, Button, Skeleton } from '@mui/material';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { getScanningStats } from '../../../../api/overView';

const StatItem = ({ value, label, color, loading }) => (
  <div className="bg-white rounded p-2 mb-2 text-sm shadow-sm">
    <div className="flex items-start gap-2">
      <FiberManualRecordIcon 
        sx={{ 
          color: color,
          fontSize: '12px',
          marginTop: '4px'
        }} 
      />
      <div style={{ width: '100%' }}>
        {loading ? (
          <>
            <Skeleton variant="text" width="40%" height={32} />
            <Skeleton variant="text" width="60%" height={20} />
          </>
        ) : (
          <>
            <div className="font-medium text-xl">{value}</div>
            <div className="text-xs text-gray-500">
              {label}
            </div>
          </>
        )}
      </div>
    </div>
  </div>
);

const ScanningStatsCard = () => {
  const { storeId } = useParams(); 
  const navigate = useNavigate();
  const [scanningStats, setScanningStats] = useState({
    totalScanned: 0,
    uniqueEANCount: 0,
    uniqueBatchCount: 0,
    scanThroughput: 0
  });
  const [loading, setLoading] = useState(true);
  const selectedDate = useSelector((state) => state.customization.selectedDate);

  useEffect(() => {
    const abortController = new AbortController();
    
    const fetchStats = async () => {
      try {
        setLoading(true);
        const response = await getScanningStats(selectedDate || new Date(), storeId, abortController.signal);
        const { summary } = response.data;
        
        setScanningStats({
          totalScanned: summary.total_product_scan || 0,
          uniqueEANCount: summary.total_ean || 0,
          uniqueBatchCount: summary.total_batch_scan || 0,
          scanThroughput: summary.average_throughput || 0
        });
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Error fetching scanning stats:', error);
          setScanningStats({
            totalScanned: 0,
            uniqueEANCount: 0,
            uniqueBatchCount: 0,
            scanThroughput: 0
          });
        }
      } finally {
        if (!abortController.signal.aborted) {
          setLoading(false);
        }
      }
    };

    fetchStats();

    return () => {
      abortController.abort();
    };
    // eslint-disable-next-line
  }, [selectedDate]);

  const { totalScanned, uniqueEANCount, uniqueBatchCount, scanThroughput } = scanningStats;

  return (
    <Grid item xs={12} sm={12} md={2.4} lg={2.4} xl={2.4}>
      <Card 
        className="border relative border-gray-300" 
        sx={{ 
          height: "276px",
          boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
          borderRadius: '8px'
        }}
      >
        <div className="flex w-full flex-col gap-1 p-3">
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-2">
              <QrCodeScannerIcon className="bg-[#2196f3] text-white rounded-full p-2 text-6xl" />
              <div style={{ width: loading ? '120px' : 'auto' }}>
                {loading ? (
                  <>
                    <Skeleton variant="text" width="100%" height={40} />
                    <Skeleton variant="text" width="80%" height={28} />
                  </>
                ) : (
                  <>
                    <p className="text-3xl">{totalScanned}</p>
                    <p className="text-lg font-semibold">Total Scanned</p>
                  </>
                )}
              </div>
            </div>
            {!loading && (
              <Button
                className="absolute text-xs right-2 top-2"
                endIcon={<ArrowForwardIcon sx={{ fontSize: "small" }} />}
                onClick={() => navigate(`/main/stores/storeinsight/tableview/${storeId}`)}
                sx={{
                  fontWeight: 600,
                  textTransform: "none",
                  color: "#666",
                  "&:hover": {
                    backgroundColor: "transparent",
                    color: "#1976d2",
                    textDecoration: "underline"
                  },
                }}
              >
                View all
              </Button>
            )}
          </div>
          <div className="bg-slate-100 flex-grow overflow-y-auto h-[184px] p-2 rounded-lg scrollbar">
            <div className="text-xs text-gray-500 mb-2 italic">
              Scanning Statistics
            </div>
            <StatItem
              value={uniqueEANCount}
              label="Unique EANs"
              color="#4CAF50"
              loading={loading}
            />
            <StatItem
              value={uniqueBatchCount}
              label="Unique Batches"
              color="#2196f3"
              loading={loading}
            />
            <StatItem
              value={scanThroughput.toFixed(2)}
              label="Products/Hour"
              color="#9C27B0"
              loading={loading}
            />
          </div>
        </div>
      </Card>
    </Grid>
  );
};

StatItem.propTypes = {
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  label: PropTypes.string.isRequired,
  color: PropTypes.string.isRequired,
  loading: PropTypes.bool
};

export default ScanningStatsCard;
